import { useRequiredContext } from "@redotech/react-util/context";
import { useStore } from "@redotech/react-util/store";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import { Store } from "@redotech/util/store";
import { localStore } from "@redotech/web-util/storage";
import { useEffect, useMemo, useState } from "react";

export const FULFILLMENT_CARD_IDS = [
  "marketplace-notification",
  "shipping-info",
  "parcel-details",
  "items",
  "parcel-photos",
  "customs-declarations",
  "additional-settings",
  "order-information",
  "assigned-user",
  "packing-slip",
  "personalized-slip",
  "notes",
  "messages",
] as const;

export type CardId = (typeof FULFILLMENT_CARD_IDS)[number];

export interface CardPreferences {
  order: string[];
  collapsedStates: Record<string, boolean>;
}

function getDefaultCardOrder(): string[] {
  return [...FULFILLMENT_CARD_IDS];
}

function getDefaultCardPreferences(): CardPreferences {
  return {
    order: getDefaultCardOrder(),
    collapsedStates: {
      "marketplace-notification": false,
      "shipping-info": false,
      "parcel-details": false,
      items: true,
      "parcel-photos": false,
      "customs-declarations": true,
      "additional-settings": false,
      "order-information": false,
      "assigned-user": true,
      "packing-slip": true,
      "personalized-slip": true,
      notes: true,
      messages: true,
    },
  };
}

function createCardPreferencesStore(userId?: string): Store<CardPreferences> {
  const baseKey = "redo.fulfillment-slideout-format";
  const userKey = userId ? `${baseKey}.${userId}` : baseKey;
  const userStore = localStore(userKey);
  const computerStore = localStore(baseKey);

  return {
    get: () => {
      try {
        // Try user-specific key first, fall back to computer key for migration
        const userStored = userId ? userStore.get() : null;
        const stored = userStored ?? computerStore.get();

        if (!stored) {
          return getDefaultCardPreferences();
        }
        const parsed = JSON.parse(stored) as CardPreferences;

        const defaults = getDefaultCardPreferences();
        const missingCards = defaults.order.filter(
          (id) => !parsed.order?.includes(id),
        );
        return {
          order: parsed.order
            ? [...parsed.order, ...missingCards]
            : defaults.order,
          collapsedStates: {
            ...defaults.collapsedStates,
            ...parsed.collapsedStates,
          },
        };
      } catch {
        return getDefaultCardPreferences();
      }
    },
    set: (value: CardPreferences) => {
      if (userId) {
        userStore.set(JSON.stringify(value));
      } else {
        computerStore.set(JSON.stringify(value));
      }
    },
  };
}

function usePersistedBoolean(key: string) {
  const [value, setValue] = useState(() => {
    return localStorage.getItem(key) === "true";
  });

  useEffect(() => {
    localStorage.setItem(key, String(value));
  }, [value, key]);

  return [value, setValue] as const;
}

export function useSlideoutConfig() {
  const user = useRequiredContext(UserContext);

  const cardPreferencesStore = useMemo(
    () => createCardPreferencesStore(user._id),
    [user._id],
  );
  const [storedPreferences, setStoredPreferences] =
    useStore(cardPreferencesStore);
  const cardPreferences = storedPreferences || getDefaultCardPreferences();

  const [addressExpanded, setAddressExpanded] = usePersistedBoolean(
    `redo-shipment-details-address-expanded.${user._id}`,
  );

  const [insuranceExpanded, setInsuranceExpanded] = usePersistedBoolean(
    `redo-shipment-details-insurance-expanded.${user._id}`,
  );

  return {
    cardPreferences,
    setCardPreferences: setStoredPreferences,
    addressExpanded,
    setAddressExpanded,
    insuranceExpanded,
    setInsuranceExpanded,
  };
}
